import React, { useEffect, useMemo, useRef } from 'react'
import { useDrop } from 'react-dnd'
import { changeNewTemplateValueRoutine } from 'features/analytics/ducks/actions'
import { useDispatch, useSelector } from 'react-redux'
import { selectAddTemplateValues } from 'features/analytics/ducks/selectors'
import styled from 'styled-components'
import { TEMPLATE_ITEMS_NAMES } from 'utils/constants'
import ClearIcon from '@mui/icons-material/Clear'
import { toast } from 'react-toastify'

const DataDropContainer = ({
  isDefaultTemplate,
  isEdit,
  dataValues,
  setDataValues,
  isEditable = true,
  isLocked = false,
  readOnlyReason,
  onDataItemDropped,
  onDataItemRemoved
}) => {
  const dispatch = useDispatch()
  const selectedValues = useSelector(selectAddTemplateValues)
  const toastCooldownRef = useRef(0)

  const [{ canDrop, isOver }, dropRef] = useDrop({
    accept: 'template-data',
    drop: (item, monitor) => {
      const isReadOnly = !isEditable
      if (isReadOnly) {
        const now = Date.now()
        if (now - toastCooldownRef.current > 1500) {
          toast.info(readOnlyReason || (isLocked ? 'This template is set as non-editable by the creator.' : 'Default checklist is read-only.'))
          toastCooldownRef.current = now
        }
        return
      }
      if (!monitor.didDrop()) {
        // Only toggle if the value isn't already set in edit mode
        if (isEdit) {
          if (!dataValues[item.value]) {
            toggleValue(item.value)
            if (typeof onDataItemDropped === 'function') {
              onDataItemDropped(item)
            }
          }
        } else {
          const willBeEnabled = !selectedValues[item.value]
          toggleValue(item.value)
          if (willBeEnabled && typeof onDataItemDropped === 'function') {
            onDataItemDropped(item)
          }
        }
      }
      return { droppedOn: 'DataDropContainer' }
    },
    collect: (monitor) => ({
      canDrop: isEditable && monitor.canDrop(),
      isOver: monitor.isOver()
    }),
    canDrop: () => isEditable
  })


  const valuesToDisplay = useMemo(() => {
    let result = []
    const values = isEdit ? dataValues : selectedValues
    for (const key in values) {
      if (values[key]) {
        result.push(key)
      }
    }
    return result
  }, [selectedValues, dataValues])

  const toggleValue = value => {
    if (isEdit) {
      setDataValues(prev => ({
        ...prev,
        [value]: !dataValues[value]
      }))
    } else {
      dispatch(changeNewTemplateValueRoutine({
        name: value,
        value: !selectedValues[value]
      }))
    }
  }

  const removeDataValue = key => {
    toggleValue(key)
    if (typeof onDataItemRemoved === 'function') {
      onDataItemRemoved(key)
    }
  }

  const handleDropAreaAttempt = () => {
    if (!isEditable) {
      const now = Date.now()
      if (now - toastCooldownRef.current > 1500) {
        toast.info(readOnlyReason || (isLocked ? 'This template is set as non-editable by the creator.' : 'Default checklist is read-only.'))
        toastCooldownRef.current = now
      }
    }
  }

  return (
    <Content
      ref={dropRef}
      $isHighlighted={canDrop && isOver}
      $isDisabled={!isEditable}
      onMouseDownCapture={handleDropAreaAttempt}
      onTouchStartCapture={handleDropAreaAttempt}
      onDragEnterCapture={handleDropAreaAttempt}
      onDragOverCapture={(e) => {
        handleDropAreaAttempt()
        e.preventDefault()
      }}
    >
      <SectionTitle>data</SectionTitle>
      {
        valuesToDisplay.map(item => {
            return (
              <SingleItem key={item}>
                <div>
                  <span>•</span>{TEMPLATE_ITEMS_NAMES[item]}
                </div>
                <CrossIcon
                  disabled={isDefaultTemplate || !isEditable}
                  onClick={
                    isDefaultTemplate || !isEditable
                      ? () => {}
                      : () => removeDataValue(item)
                  }
                />
              </SingleItem>
            )
          })
      }
      <>
        <DropHint>
          {isEditable ? 'Drag data fields here' : (isLocked ? 'Template is non-editable' : 'Template is read-only')}
        </DropHint>
        <DropContainer
          isHighlighted={canDrop && isOver}
          onMouseDown={handleDropAreaAttempt}
          onTouchStart={handleDropAreaAttempt}
        />
      </>
    </Content>
  )
}

export default DataDropContainer

const Content = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundColor};
  padding: 20px;
  border-radius: 6px;
  transition: 0.2s all;
  min-height: 120px;
  position: relative;
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.6 : 1)};

  @media (min-width: 769px) {
    border: 2px dashed
      ${({ theme, $isHighlighted }) =>
        $isHighlighted ? theme.colors.primary : 'rgba(0,0,0,0.08)'};
    background: ${({ $isHighlighted }) =>
      $isHighlighted ? '#eaf9fa' : 'transparent'};
  }
`

const SingleItem = styled.div`
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;

  span {
    display: inline-block;
    color: ${({ theme }) => theme.colors.secondary};
    margin-right: 10px;
  }
`

const SectionTitle = styled.div`
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.secondary};
  margin-bottom: 10px;
`

const CrossIcon = styled(ClearIcon)`
  font-size: 14px !important;
  cursor: pointer;
  color: ${({ disabled, theme }) =>  disabled ? theme.colors.grey[600] : theme.colors.primary};
`

const DropHint = styled.div`
  margin-top: 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[600]};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;

  @media (max-width: 768px) {
    display: none;
  }
`

const DropContainer = styled.div`
  display: block;
  width: 100%;
  height: 40px;
  border-radius: 4px;
  border: 1px dashed ${({ theme }) => theme.colors.grey[600]};
  margin-top: 20px;
  transition: .3s all;
  background-color: ${({ isHighlighted }) => isHighlighted ? '#eaf9fa' : 'transparent'};

  @media (min-width: 769px) {
    display: none;
  }
`
